<script setup lang="ts">
  import { ref } from 'vue';
  import { Tippy } from 'vue-tippy';
  import { IconImage } from '@/shared/ui';

  interface InfoTooltipEntry {
    label?: string;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
  }

  interface Props {
    title?: string;
    icon: string;
    createdAt?: string;
    createdBy?: string;
    updatedAt?: string;
    updatedBy?: string;
    entries?: InfoTooltipEntry[];
    placement?: 'top' | 'bottom' | 'left' | 'right';
    lazy?: boolean;
    /**
     * Tippy binds `focus` to the reference element and focus does not bubble, so
     * a card whose reference wraps a focusable control needs `focusin` to answer
     * the keyboard. The default keeps the tippy behaviour every caller had.
     */
    trigger?: string;
  }

  withDefaults(defineProps<Props>(), {
    title: 'Informações',
    placement: 'top',
    lazy: true,
    trigger: 'mouseenter focus',
  });

  const isTooltipReady = ref(false);

  const prepareTooltip = () => {
    isTooltipReady.value = true;
  };
</script>

<template>
  <Tippy
    :placement="placement"
    :trigger="trigger"
    theme="custom"
    class="flex items-center"
    @mouseenter="prepareTooltip"
    @focusin="prepareTooltip"
  >
    <template #default>
      <slot />
    </template>
    <template #content>
      <div v-if="!lazy || isTooltipReady">
        <div class="flex items-center mb-3">
          <IconImage
            :src="icon"
            :alt="title"
          />
          <p class="ml-2.5 font-semibold text-sm">
            {{ title }}
          </p>
        </div>
        <template v-if="entries && entries.length > 0">
          <div
            v-for="(entry, index) in entries"
            :key="index"
            class="border-t border-border-color"
          >
            <p
              v-if="entry.label"
              class="px-6 pt-4 font-semibold text-sm text-gray-07"
            >
              {{ entry.label }}
            </p>
            <div class="grid grid-cols-4 items-start gap-x-8 gap-y-2.5 p-6 text-gray-07">
              <div>
                <p class="mb-1 font-semibold text-sm">
                  Criado em
                </p>
                <p>{{ entry.createdAt ?? '-' }}</p>
              </div>
              <div>
                <p class="mb-1 font-semibold text-sm">
                  Criado por
                </p>
                <p
                  data-testid="info-tooltip-created-by-value"
                  class="whitespace-normal break-words"
                >
                  {{ entry.createdBy ?? '-' }}
                </p>
              </div>
              <div>
                <p class="mb-1 font-semibold text-sm">
                  Editado por
                </p>
                <p
                  data-testid="info-tooltip-updated-by-value"
                  class="whitespace-normal break-words"
                >
                  {{ entry.updatedBy ?? '-' }}
                </p>
              </div>
              <div>
                <p class="mb-1 font-semibold text-sm">
                  Editado em
                </p>
                <p>{{ entry.updatedAt ?? '-' }}</p>
              </div>
            </div>
          </div>
        </template>
        <div
          v-else
          class="border-t border-border-color"
        >
          <div class="grid grid-cols-4 items-start gap-x-8 gap-y-2.5 p-6 text-gray-07">
            <div>
              <p class="mb-1 font-semibold text-sm">
                Criado em
              </p>
              <p>{{ createdAt ?? '-' }}</p>
            </div>
            <div>
              <p class="mb-1 font-semibold text-sm">
                Criado por
              </p>
              <p
                data-testid="info-tooltip-created-by-value"
                class="whitespace-normal break-words"
              >
                {{ createdBy ?? '-' }}
              </p>
            </div>
            <div>
              <p class="mb-1 font-semibold text-sm">
                Editado por
              </p>
              <p
                data-testid="info-tooltip-updated-by-value"
                class="whitespace-normal break-words"
              >
                {{ updatedBy ?? '-' }}
              </p>
            </div>
            <div>
              <p class="mb-1 font-semibold text-sm">
                Editado em
              </p>
              <p>{{ updatedAt ?? '-' }}</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Tippy>
</template>
